const csrfToken = getCSRFToken();

/**
 * Fetch a resource.
 * Automatically adds csrf token and content-type for POST requests
 *
 * @param {String} url
 * @param {RequestInit} init
 *
 * @returns {Promise<Response>}
 */
export async function fetch(url, init = {}) {
  let headers = new Headers(init.headers);
  headers.append("x-csrf-token", csrfToken);
  headers.append("x-phxprof-token", window.getPhxProfToken?.());

  // Handle jQuery style params to transform to query string
  if (init.method?.toUpperCase() === "GET" && typeof init.params === "object") {
    const params = new URLSearchParams(init.params);
    url = `${url}?${params.toString()}`;
  }

  return window.fetch(url, { ...init, headers });
}

/**
 * Fetch a JSON response.
 * Automatically adds csrf token and content-type for POST requests
 *
 * @param {String} url
 * @param {RequestInit} init
 *
 * @returns {Promise<Object>}
 */
export async function fetchJSON(url, init = {}) {
  let headers = new Headers(init.headers);
  let fetchInit = { ...init };

  if (!headers.has("accept")) {
    headers.append("accept", "application/json");
  }

  if (init.method?.toUpperCase() === "POST" || init.method?.toUpperCase() === "PUT") {
    if (!headers.has("content-type")) {
      headers.append("content-type", "application/json; charset=utf-8");
    }

    // a little helper to stringify `body` if it is a JSON object
    if (typeof init.body === "object") {
      fetchInit.body = JSON.stringify(init.body);
    }
  }

  fetchInit.headers = headers;

  const response = await fetch(url, fetchInit);
  const data = await response.json();

  if (!response.ok) {
    if (response.status === 403 && data.redirect) {
      window.location.href = data.redirect;
      return;
    } else {
      throw new APIError(data);
    }
  }

  return data;
}

/**
 * Poll an URL, requiring a JSON response, until a predicate is met.
 * The poll continues while the predicates returns a falsy value.
 * The predicate receives the request's response as a parameter.
 *
 * @param {(Object) => boolean} predicate
 * @param {String} url
 * @param {RequestInit} init
 * @param {Number} timeout_ms
 *
 * @returns {Object} The response from the last request.
 */
export async function pollJSONUntil(predicate, url, init = undefined, timeout_ms = 5000) {
  while (true) {
    const response = await fetchJSON(url, init);
    if (predicate(response)) {
      return response;
    }
    await sleep(timeout_ms);
  }
}

async function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

export class APIError extends Error {
  constructor(data) {
    super(data.reason);

    /** @type {Number} */
    this.code = data.code;
    /** @type {Object} */
    this.data = data;
  }
}

export function getCSRFToken() {
  return document.querySelector("meta[name=csrf]").content;
}

export function initForAlpine(Alpine) {
  Alpine.magic("api", () => ({
    fetch,
    fetchJSON,
    pollJSONUntil,
    getCSRFToken,
  }));
}
